import React, { useEffect, useRef, useState } from "react";
import { api, formatError, BACKEND_URL } from "../lib/api";
import { toast } from "sonner";
import { UploadCloud, Sparkles, X, Loader2, Image as ImageIcon, RefreshCw } from "lucide-react";

/**
 * Blueprint image field — three ways to add a logo / packaging shot:
 *   1. Click to upload
 *   2. Drag-and-drop
 *   3. Pick from AI Studio generations saved to Projects
 *
 * Value is stored as a URL string (relative /api/… paths for both uploads and
 * AI-studio-generation refs). Rendered inline via <img src=BACKEND_URL+value>.
 */
export default function ImagePickerField({ value, onChange, assetKind, testid }) {
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileRef = useRef(null);

  const src = value
    ? (value.startsWith("http") ? value : `${BACKEND_URL}${value}`)
    : "";

  const handleUpload = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please pick an image (PNG, JPG, WEBP, or GIF)");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image is too large (8 MB max)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/blueprint/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(r.data.url);
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  };

  return (
    <div data-testid={testid}>
      {src ? (
        <div className="mt-1.5 relative rounded-xl border border-shell-border bg-muted/30 overflow-hidden max-w-md">
          <img src={src} alt={assetKind}
            className="block w-full max-h-72 object-contain bg-white"
            data-testid={`${testid}-preview`} />
          <div className="absolute top-2 right-2 flex gap-1.5">
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              data-testid={`${testid}-replace`}
              className="text-xs font-semibold bg-white/90 hover:bg-white border border-shell-border rounded-full px-3 py-1 inline-flex items-center gap-1 backdrop-blur">
              <RefreshCw className="w-3 h-3" /> Replace
            </button>
            <button onClick={() => setPickerOpen(true)} disabled={uploading}
              data-testid={`${testid}-pick-project`}
              className="text-xs font-semibold bg-white/90 hover:bg-white border border-shell-border rounded-full px-3 py-1 inline-flex items-center gap-1 backdrop-blur">
              <Sparkles className="w-3 h-3" /> From Projects
            </button>
            <button onClick={() => onChange("")} disabled={uploading}
              data-testid={`${testid}-remove`}
              className="text-xs font-semibold bg-white/90 hover:bg-white border border-shell-border text-red-600 rounded-full px-3 py-1 inline-flex items-center gap-1 backdrop-blur">
              <X className="w-3 h-3" /> Remove
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) handleUpload(f);
          }}
          className={[
            "mt-1.5 rounded-xl border-2 border-dashed p-6 text-center transition",
            dragging ? "border-primary bg-primary/5" : "border-shell-border bg-muted/30 hover:bg-muted/60",
          ].join(" ")}
          data-testid={`${testid}-dropzone`}
        >
          {uploading ? (
            <p className="text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
            </p>
          ) : (
            <>
              <UploadCloud className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="mt-2 text-sm font-semibold">Drag an image here or</p>
              <div className="mt-3 inline-flex flex-wrap gap-2 justify-center">
                <button onClick={() => fileRef.current?.click()}
                  data-testid={`${testid}-upload-btn`}
                  className="btn-primary text-xs">
                  <UploadCloud className="w-3.5 h-3.5" /> Upload from device
                </button>
                <button onClick={() => setPickerOpen(true)}
                  data-testid={`${testid}-pick-btn`}
                  className="btn-amber text-xs">
                  <Sparkles className="w-3.5 h-3.5" /> Pick from AI Studio
                </button>
              </div>
              <p className="mt-3 text-[11px] text-muted-foreground">
                PNG · JPG · WEBP · GIF, up to 8 MB
              </p>
            </>
          )}
        </div>
      )}
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden" data-testid={`${testid}-file`}
        onChange={(e) => {
          const f = e.target.files?.[0]; e.target.value = "";
          if (f) handleUpload(f);
        }} />
      {pickerOpen && (
        <AiStudioPickerModal
          assetKind={assetKind}
          onPick={(url) => { onChange(url); setPickerOpen(false); toast.success("Image linked from Projects"); }}
          onClose={() => setPickerOpen(false)}
          testid={`${testid}-picker`}
        />
      )}
    </div>
  );
}

function AiStudioPickerModal({ assetKind, onPick, onClose, testid }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get(`/blueprint/asset-picker/${assetKind}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [assetKind]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center px-4" data-testid={testid}
         onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl border border-shell-border max-w-4xl w-full max-h-[80vh] flex flex-col overflow-hidden">
        <div className="px-6 py-4 border-b border-shell-border flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Your AI Studio</p>
            <h2 className="font-display text-xl">Pick a saved {assetKind} concept</h2>
          </div>
          <button onClick={onClose} data-testid={`${testid}-close`}
            className="rounded-full p-2 hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {items === null ? (
            <div className="text-sm text-muted-foreground inline-flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your projects…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-8">
              <ImageIcon className="w-10 h-10 text-muted-foreground mx-auto" />
              <p className="mt-3 text-sm text-muted-foreground max-w-sm mx-auto">
                No {assetKind} concepts yet. Head to AI Studio → {assetKind === "logo" ? "Logo Designer" : "Packaging Designer"} to generate a few, then come back and pick your favourite.
              </p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3">
              {items.map((it, idx) => (
                <button key={`${it.generation_id}-${it.image_index}`}
                  onClick={() => onPick(it.url)}
                  data-testid={`${testid}-item-${idx}`}
                  className="group text-left rounded-xl border border-shell-border overflow-hidden hover:border-primary hover:shadow-lg transition">
                  <img src={`${BACKEND_URL}${it.url}`} alt={it.prompt}
                    className="block w-full aspect-square object-cover bg-muted" />
                  <div className="px-3 py-2">
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {it.prompt || "AI Studio generation"}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
